import { DLC_SM_CHAR_WIDTH, DLC_MD_CHAR_WIDTH, DLC_FULL_WIDTH, DLC_HALF_WIDTH } from "profiles-new/constants"
import classNames from "classnames"

/**
 * Helper functions that imperfectly determine which browser is currently being used by the client.
 * Refer to the link: https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
 * to understand the approach and its limitations. If we want to further expand upon using browser detection,
 * expand to using npm dependencies such as bowser or react-bowser-detection.
 */
// Safari 3.0+ "[object HTMLElementConstructor]"
export const isSafari = () => navigator && navigator.userAgent && navigator.userAgent.indexOf("Safari") != -1
// Chrome 1 - 79
export const isChrome = () => window && !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)
// Firefox 1.0+
export const isFirefox = () => typeof InstallTrigger !== "undefined"

/**
 * Given a title, subtitle and the width of the DLC section, return whether the given section
 * should have a quarter sized DTV pill by determining whether the subtitle or title is less than DLC_SM_CHAR_WIDTH
 * @param  {string} title [The title of the DTV pill]
 * @param  {string} subtitle [The text inside of the DTV pill]
 * @param  {number} width [The current width of the DLC section, must be 1 or 2]
 * @return {bool} [The bool result of whether it fits in a 1/4 pill relative to the DLC]
 */
export const isDlcQuarterPill = ({ title, subtitle, width }) => {
    if (width === DLC_FULL_WIDTH) {
        return (
            (subtitle && subtitle.length < DLC_SM_CHAR_WIDTH && subtitle.indexOf("\n") === -1) ||
            (title && title.length < DLC_SM_CHAR_WIDTH && title.indexOf("\n") === -1)
        )
    }
    return false
}

/**
 * Given a title, subtitle and the width of the DLC section, return whether the given section
 * should have a half sized DTV pill for the DLC by determining whether the subtitle or title
 * is less than DLC_MD_CHAR_WIDTH. If the DLC section is half width,
 * assume that it is solving isDlcQuarterPill for FULL_WIDTH because we effectively want to
 * double the bootstrap value for half-width sections
 * @param  {string} subtitle [The text inside of the DTV pill]
 * @param  {number} width [The current width of the DLC section, must be 1 or 2]
 * @return {bool} [The bool result of whether it fits in a 1/2 pill relative to the DLC]
 */
export const isDlcHalfPill = ({ title, subtitle, width }) => {
    if (width === DLC_FULL_WIDTH) {
        return (
            (subtitle &&
                subtitle.length < DLC_MD_CHAR_WIDTH &&
                subtitle.length >= DLC_SM_CHAR_WIDTH &&
                subtitle.indexOf("\n") === -1) ||
            (title &&
                title.length < DLC_MD_CHAR_WIDTH &&
                title.length >= DLC_SM_CHAR_WIDTH &&
                title.indexOf("\n") === -1)
        )
    } else if (width === DLC_HALF_WIDTH) {
        return isDlcQuarterPill({ title, subtitle, width: DLC_FULL_WIDTH })
    }
    return false
}

/**
 * Given a title, subtitle and the width of the DLC section, return whether the given section
 * should have a full sized DTV pill for the DLC by determining whether the subtitle or
 * title is larger than DLC_MD_CHAR_WIDTH If the DLC section is half width,
 * assume that it is solving isDlcHalfPill for FULL_WIDTH because we effectively want to
 * double the bootstrap value for half-width sections
 * @param  {string} subtitle [The text inside of the DTV pill]
 * @param  {number} width [The current width of the DLC section, must be 1 or 2]
 * @return {bool} [The bool result of whether it fits in a full sized pill relative to the DLC]
 */
export const isDlcFullPill = ({ title, subtitle, width }) => {
    if (width === DLC_FULL_WIDTH) {
        return (
            (subtitle && subtitle.length >= DLC_MD_CHAR_WIDTH) ||
            subtitle.indexOf("\n") > -1 ||
            (title && title.length >= DLC_MD_CHAR_WIDTH) ||
            title.indexOf("\n") > -1
        )
    } else if (width === DLC_HALF_WIDTH) {
        return (
            (subtitle && subtitle.length >= DLC_SM_CHAR_WIDTH) ||
            subtitle.indexOf("\n") > -1 ||
            (title && title.length >= DLC_SM_CHAR_WIDTH) ||
            title.indexOf("\n") > -1
        )
    }
    return false
}

/**
 * Given a subtitle and the width of the DLC section, return whether the given section
 * should require two lines of content. For now, we only want to show two lines for
 * dlc half width pills that have a full width of content.
 * @param  {string} subtitle [The text inside of the DTV pill]
 * @param  {number} width [The current width of the DLC section, must be 1 or 2]
 * @return {bool} [The bool result of whether it fits in a full sized pill relative to the DLC]
 */
export const isDlcTwoLinePill = (subtitle, width) => {
    if (width === DLC_HALF_WIDTH) {
        return (subtitle && subtitle.length >= DLC_MD_CHAR_WIDTH) || subtitle.indexOf("\n") > -1
    }
    return false
}

/**
 * Given props with width, subtitle, and char widths, return the proper classnames
 * that will correctly determine the correct sized pill for a given subtitle for use in a DTV.
 * @param  {object} props
 *     @param  {string} subtitle [The text inside of the DTV pill]
 *     @param  {number} width [A fixed width that should be applied regardless subtitle length]
 *     @param  {number} sm [The minimum char width for the smallest pill]
 *     @param  {number} md [The minimum char width for the medium pill]
 *     @param  {number} lg [The minimum char width for the largest pill]
 * @return {bool} [The proper classnames for DTVs produced in the DLC.]
 */
export const getClasses = (props) =>
    classNames({
        // currently always want the smaller screens to be taken up by 1 item only
        "col-xs-12": true,
        "col-sm-12": true,
        /*
        if there is a width, just set the bootstrap column to be that width

        if the subtitle has a newline, make the dataitem full width regardless

        if there is no width, estimate what the width should be based on the
        character length of the subtitle. This is based on the estimated
        character lengths for the sections on a 13 in scheen.
    */
        "col-md-4":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.sm &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 3,
        "col-lg-3":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.sm &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 3,

        "col-md-6":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.md &&
                props.subtitle.length >= props.sm &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 6,
        "col-lg-6":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.md &&
                props.subtitle.length >= props.sm &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 6,

        "col-md-9":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.lg &&
                props.subtitle.length >= props.md &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 9,
        "col-lg-9":
            (!props.width &&
                props.subtitle &&
                props.subtitle.length < props.lg &&
                props.subtitle.length >= props.md &&
                props.subtitle.indexOf("\n") === -1) ||
            props.width === 9,

        "col-md-12":
            (!props.width && props.subtitle && props.subtitle.length >= props.lg) ||
            props.subtitle.indexOf("\n") > -1 ||
            props.width === 12,
        "col-lg-12":
            (!props.width && props.subtitle && props.subtitle.length >= props.lg) ||
            props.subtitle.indexOf("\n") > -1 ||
            props.width === 12,
    })

/**
 * Given a title, subtitle and the width of the DLC section, return the proper classnames
 * that will correctly determine the correct sized pill for a given subtitle.
 * With the introduction of title + subtitle calculations, we determine the size by
 * the choosing the largest sized pill that fits both the title and subtitle.
 * @param  {string} subtitle [The text inside of the DTV pill]
 * @param  {number} width [The current width of the DLC section, must be 1 or 2]
 * @return {bool} [The proper classnames for DTVs produced in the DLC.]
 */
export const getDlcClasses = ({ title, subtitle, width }) => {
    const isQuarter = isDlcQuarterPill({ title, subtitle, width })
    const isHalf = isDlcHalfPill({ title, subtitle, width })
    const isFull = isDlcFullPill({ title, subtitle, width })

    return classNames(
        // className for all the data items if it is being rendered in DLC
        "dlc-data-item",
        {
            /*
            For the download center, we do not want the dtv sections to autosize because
            we are creating a fixed pdf.

            The width of a dtv pill is currently being determined by the size of the text.
            and the width of the section container. This is a rough guess of the correct size needed,
            and might cause issues with different font styles.

            If the width is reduced down to one, the pill size is doubled by 2, (except for 12)
            3 -> 6, 6 -> 12, 12 -> 12.
        */
            "col-xs-3 col-sm-3 col-md-3 col-lg-3": isQuarter && !isHalf && !isFull,
            "col-xs-6 col-sm-6 col-md-6 col-lg-6": isHalf && !isFull,
            "col-xs-12 col-sm-12 col-md-12 col-lg-12": isFull,

            "dlc-two-line": isDlcTwoLinePill(subtitle, width),
        },
    )
}

/**
 * Given an array of custom fields information, return the properly structured object of groups in the shape of:
 * {
 *  [custom_field_group_resource_url] : {
 *     name: "string of custom field group name",
 *     precedence: integer of the order of appearance,
 *     custom_fields: [array of custom field objects (strcutured the same as in data) that belong to this group],
 *   }
 * }
 * @param  {array} data [An array of custom fields]
 * @return {array} [An array of `group` objects which has `name`, `precedence`, and `custom_fields` ]
 */
export const getCustomFieldGroups = (data) => {
    const cf_groups = {
        no_group: {
            name: "No Group",
            precedence: null,
            custom_fields: [],
        },
    }
    data.forEach((cf) => {
        if (cf.group) {
            if (!cf_groups[cf.group]) {
                cf_groups[cf.group] = {
                    name: cf.group_dict.group__name,
                    precedence: cf.group_dict.group__precedence,
                    custom_fields: [cf],
                }
            } else {
                cf_groups[cf.group].custom_fields.push(cf)
            }
        } else {
            cf_groups.no_group.custom_fields.push(cf)
        }
    })

    if (!cf_groups.no_group.custom_fields.length) {
        delete cf_groups.no_group
    }
    return cf_groups
}

/**
 * Given an array of custom fields information,
 * return the structured object of groups sorted based on:
 * 1. Precedence
 * 2. Name
 * Should return the data param if none of the custom fields belong to any groups
 * @param  {array} data [An array of custom fields]
 * @return {array} [A sorted array of objects which has `name`, `precedence`, and `custom_fields` ]
 * or return the data param if there's no group found
 */
export const getCustomFieldGroupArray = (data) => {
    const groupObj = getCustomFieldGroups(data)
    const groupArray = Object.values(groupObj)
    if (groupArray.length === 1 && groupArray[0].name === "No Group") {
        // return null and let
        // app/static/frontend/profiles-new/components/DataTableView/index.jsx
        // uses "data" to render
        return null
    }
    // the group with higher precedence should appear first.
    // This is to keep the same logic as in sheet app/static/frontend/sheets/sheet-action-creators.jsx
    groupArray.sort((a, b) => {
        const numA = a.precedence || a.precedence === 0
        const numB = b.precedence || b.precedence === 0
        if (numA && numB) {
            if (a.precedence !== b.precedence) {
                return b.precedence - a.precedence
            }
            return a.name > b.name ? 1 : -1
        } else if (numA) {
            return -1
        } else if (numB) {
            return 1
        }

        return a.name - b.name
    })

    return groupArray
}
